import React, { useState } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { BarChart3, Table, Bell, LogOut } from 'lucide-react';
import FormaliteTable from './components/FormaliteTable';
import Dashboard from './components/Dashboard';
import { LoginPage } from './components/LoginPage';
import { useDeadlineMonitor } from './hooks/useDeadlineMonitor';
import { useAuth } from './contexts/AuthContext';
import { useFormalitesCache } from './contexts/FormalitesContext';

type ViewMode = 'dashboard' | 'table';

interface SelectedDeadlineFilter {
  entreprise?: string;
  client?: string;
  type?: string;
}

function App() {
  const [viewMode, setViewMode] = useState<ViewMode>('dashboard');
  const [targetRowId, setTargetRowId] = useState<number | null>(null);
  const [selectedDeadlineFilter, setSelectedDeadlineFilter] = useState<SelectedDeadlineFilter | null>(null);
  const { isAuthenticated, logout, user, loading: authLoading } = useAuth();
  
  // Utiliser le cache global des formalités
  const {
    allData,
    loading: dataLoading,
    error: dataError,
    refresh: refreshData,
  } = useFormalitesCache();

  // Surveillance des échéances avec notifications automatiques (sur toutes les données)
  const { forceCheck, getNotificationCount } = useDeadlineMonitor({
    data: allData,
    enabled: true,
    checkInterval: 60 * 60 * 1000, // Vérifier toutes les heures
    notifyOnLoad: true
  });

  // Afficher un écran de chargement pendant l'initialisation
  if (authLoading || dataLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {authLoading ? 'Authentification...' : 'Chargement des données...'}
          </p>
        </div>
      </div>
    );
  }

  const notificationCount = getNotificationCount();

  const handleRefresh = async () => {
    await refreshData(); // Recharge tout depuis l'API
    await forceCheck(); // Vérifier les notifications après rafraîchissement
  };

  const handleNavigateToDeadline = (filter: SelectedDeadlineFilter) => {
    setSelectedDeadlineFilter(filter);
    setTargetRowId(null);
    setViewMode('table');
  };

  // Composant pour le contenu du Dashboard (avec navigation)
  const DashboardLayout = () => (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-8">
              <h1 className="text-xl font-bold text-gray-900">
                Dashboard Formalités
              </h1>
              
              <nav className="flex space-x-4">
                <button
                  onClick={() => {
                    setSelectedDeadlineFilter(null);
                    setTargetRowId(null);
                    setViewMode('dashboard');
                  }}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'dashboard'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Tableau de bord
                </button>
                
                <button
                  onClick={() => {
                    setSelectedDeadlineFilter(null);
                    setTargetRowId(null);
                    setViewMode('table');
                  }}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'table'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Table className="w-4 h-4 mr-2" />
                  Liste détaillée
                </button>
              </nav>
            </div>

            <div className="flex items-center space-x-4">
              {/* Indicateur de notifications */}
              {notificationCount > 0 && (
                <div className="flex items-center px-3 py-2 bg-red-100 text-red-700 rounded-md">
                  <Bell className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">
                    {notificationCount} échéance{notificationCount > 1 ? 's' : ''} urgente{notificationCount > 1 ? 's' : ''}
                  </span>
                </div>
              )}

              {/* Informations utilisateur */}
              <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 rounded-md">
                <span className="text-sm font-medium text-gray-700">
                  {user?.field_7278}
                </span>
              </div>

              {/* Bouton de rafraîchissement */}
              <button
                onClick={handleRefresh}
                disabled={dataLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
              >
                {dataLoading ? 'Chargement...' : 'Actualiser'}
              </button>

              {/* Bouton de déconnexion */}
              <button
                onClick={logout}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="container mx-auto px-4 py-8">
        {dataError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Erreur de chargement
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{dataError}</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={handleRefresh}
                    className="bg-red-100 px-2 py-1 rounded-md text-red-800 hover:bg-red-200 transition-colors"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'dashboard' ? (
          <div className="space-y-8">
            {/* Dashboard principal avec statistiques */}
            <Dashboard 
              data={allData} 
              loading={dataLoading} 
              onDataUpdated={handleRefresh}
              onNavigateToDeadline={handleNavigateToDeadline}
            />
            
            {/* Information sur les notifications */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <Bell className="h-5 w-5 text-blue-400 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Notifications automatiques activées
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      Les notifications par email sont envoyées automatiquement à{' '}
                      <strong>pjleroy@webapic.com</strong> pour :
                    </p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Échéances dépassées (immédiatement)</li>
                      <li>Échéances dans moins de 24h (immédiatement)</li>
                      <li>Échéances dans moins de 3 jours (rapport quotidien)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <FormaliteTable 
            targetRowId={targetRowId}
            selectedDeadlineFilter={selectedDeadlineFilter}
            clearSelectedDeadlineFilter={() => setSelectedDeadlineFilter(null)}
            resetTarget={() => {
              setTargetRowId(null);
            }}
          />
        )}
      </div>
    </div>
  );

  // Retourner les routes
  return (
    <Routes>
      {/* Route de connexion */}
      <Route path="/login" element={<LoginPage />} />
      
      {/* Route du dashboard - requiert authentification */}
      <Route 
        path="/dashboard" 
        element={isAuthenticated ? <DashboardLayout /> : <Navigate to="/login" replace />} 
      />
      
      {/* Route racine - redirection automatique */}
      <Route 
        path="/" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />} 
      />
      
      {/* Route par défaut - 404 redirige vers login ou dashboard selon l'état */}
      <Route 
        path="*" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />} 
      />
    </Routes>
  );
}

export default App;
